// HDB / EC purchase-eligibility check by household income. This is the
// *eligibility* gate (are you allowed to buy this?), distinct from the
// *affordability* math (can you finance it?) in affordability.ts. Pure,
// browser-only, decision support — not advice.
//
// Figures are Singapore's monthly household GROSS income ceilings, reflecting
// the changes that took effect 24 Aug 2026 (raised from the 2019 levels).
// Verified against the HDB & CPF primary pages via the "Fetch eligibility data"
// GitHub workflow (scripts/fetch-eligibility.mjs) — re-run it and update these
// constants when policy changes.

import type { Profile } from "../profile/schema";

export const ELIGIBILITY = {
  asOf: "2026-08-24",
  source: "HDB & CPF Board (income-ceiling changes effective 24 Aug 2026)",
  sourceUrl:
    "https://www.hdb.gov.sg/hdb-pulse/news/2026/increase-in-income-ceilings-and-greater-support-for-families-with-children",

  // New flat bought from HDB (BTO / SBF).
  hdbNewFlat: {
    family: 16_000, // 3-room (typical), 4-/5-room, Executive
    familyTwoRoomFlexi: 8_000, // 99-year lease (short-lease seniors 2-room = 16,000)
    familyExtendedMultigen: 24_000, // buying with extended/multi-gen family; each nucleus still <= 16,000
    threeRoomLowerCeiling: 8_000, // some 3-room projects apply this instead of 16,000
    single: 8_000, // singles 35+, 2-room Flexi (and 3-room in some projects)
    singleMinAge: 35,
  },

  // Resale flat: NO income ceiling to buy with a bank loan. The ceiling below
  // only gates CPF housing grants (excluding the Proximity Housing Grant) and an
  // HDB housing loan. Under the Standard/Plus/Prime framework, Plus resale flats
  // are also capped at 16,000 and 2-room Prime resale at 8,000.
  hdbResaleGrantLoan: {
    family: 16_000,
    single: 8_000,
  },

  // Executive Condominium bought new from a developer.
  ec: {
    standard: 16_000, // existing ECs / land-sale tenders awarded before 24 Aug 2026
    newTender: 18_000, // land-sale tenders closing on/after 24 Aug 2026
  },

  // Enhanced CPF Housing Grant — this ceiling governs the GRANT amount, not
  // whether you may buy. Purchase eligibility can be higher.
  ehg: {
    family: 9_000, // >=2 first-timer applicants; halved to 4,500 with a second-timer
    single: 4_500, // 9,000 if buying with other singles / a resale flat with parents
  },
} as const;

export type ApplicantType = "family" | "single";

/** A single applicant is inferred from a one-adult household. */
export function applicantType(p: Profile): ApplicantType {
  return (p.household?.adults ?? 1) <= 1 ? "single" : "family";
}

export interface CeilingCheck {
  scheme: string; // human-readable label
  ceiling: number; // monthly household gross income ceiling ($)
  income: number; // the household income compared against it
  eligible: boolean; // income <= ceiling
  deltaMonthly: number; // headroom (+) or overage (-) vs the ceiling
  note?: string; // caveat (project-dependent, phase-in, grant-only, etc.)
}

export interface HdbEcEligibility {
  income: number;
  applicant: ApplicantType;
  flatType: string; // target HDB flat type (uppercased), or "" if unset
  incomeKnown: boolean; // false when no household income has been entered
  singleUnderAge: boolean; // single applicant below the 35-year age gate
  hdbNew: CeilingCheck; // buying a new flat from HDB (BTO / SBF)
  hdbResaleGrant: CeilingCheck; // CPF grant / HDB loan on a resale flat
  ec: CeilingCheck; // new EC from a developer
  ehg: CeilingCheck; // EHG grant-amount tier (informational)
}

/**
 * Evaluate HDB/EC purchase eligibility for the household. `flatTypeOverride`
 * lets a panel pass the live flat-type selection instead of the stored target.
 */
export function hdbEcEligibility(p: Profile, flatTypeOverride?: string): HdbEcEligibility {
  const income = Math.max(0, p.monthlyGrossIncome || 0);
  const incomeKnown = income > 0;
  const applicant = applicantType(p);
  const isSingle = applicant === "single";
  const flatType = (flatTypeOverride ?? p.targetHdbFlatType ?? p.hdbFilterFlatType ?? "")
    .toUpperCase()
    .trim();

  const mk = (scheme: string, ceiling: number, note?: string): CeilingCheck => ({
    scheme,
    ceiling,
    income,
    eligible: income <= ceiling,
    deltaMonthly: ceiling - income,
    note,
  });

  const N = ELIGIBILITY.hdbNewFlat;

  // New flat from HDB — ceiling depends on applicant type and flat type.
  let hdbNew: CeilingCheck;
  if (isSingle) {
    hdbNew = mk("New flat from HDB (singles scheme)", N.single,
      "Singles: aged 35+, 2-room Flexi (some projects also offer 3-room, at this $8,000 ceiling).");
  } else if (flatType === "2 ROOM") {
    hdbNew = mk("New 2-room Flexi from HDB", N.familyTwoRoomFlexi,
      "99-year lease. A short-lease 2-room (seniors) uses a $16,000 ceiling.");
  } else if (flatType === "3 ROOM") {
    hdbNew = mk("New 3-room flat from HDB", N.family,
      "Project-dependent: some 3-room projects apply an $8,000 ceiling instead — check the launch press release. $24,000 with extended/multi-gen family.");
  } else if (flatType) {
    hdbNew = mk(`New ${flatType} flat from HDB`, N.family,
      `S$${N.familyExtendedMultigen.toLocaleString()} when buying with extended / multi-generation family.`);
  } else {
    hdbNew = mk("New flat from HDB", N.family,
      "For 3-room and larger. 2-room Flexi is $8,000; $24,000 with extended/multi-gen family.");
  }

  const hdbResaleGrant = mk(
    "Resale flat — CPF grant / HDB loan",
    isSingle ? ELIGIBILITY.hdbResaleGrantLoan.single : ELIGIBILITY.hdbResaleGrantLoan.family,
    "No income ceiling to buy a resale flat with a bank loan — this only gates CPF housing grants (excl. Proximity) and an HDB housing loan. Plus-model resale flats are also capped here.",
  );

  const ec = mk("New Executive Condominium (from developer)", ELIGIBILITY.ec.standard,
    `Rises to S$${ELIGIBILITY.ec.newTender.toLocaleString()} for ECs from land-sale tenders closing on/after 24 Aug 2026. Resale / privatised ECs have no income ceiling.`);

  const ehg = mk("Enhanced CPF Housing Grant (grant amount)",
    isSingle ? ELIGIBILITY.ehg.single : ELIGIBILITY.ehg.family,
    "Governs the GRANT amount, not whether you may buy. " +
      (isSingle
        ? "$9,000 when buying with other singles or a resale flat with your parents."
        : "Halved to $4,500 if applying with a second-timer."));

  return {
    income,
    applicant,
    flatType,
    incomeKnown,
    singleUnderAge: isSingle && (p.ageYears || 0) < N.singleMinAge,
    hdbNew,
    hdbResaleGrant,
    ec,
    ehg,
  };
}
